import { useEffect, useRef, useState } from "react";
import { DowntimeReasonChoice } from "./DowntimeReasonChoice";
import type { ProductionReason } from "./models";
import { useStoredOperatorDraft } from "./useStoredOperatorDraft";
import { reasonFormDraftSchema } from "./operator-drafts";
import { downtimeReasonRequest, downtimeReasonSubmission, type DowntimeReasonRequest } from "./downtime-reason-submission";
import { DowntimeQuickReasons } from "./DowntimeQuickReasons";
import { DowntimeKeyboardField } from "./DowntimeKeyboardField";
import { DowntimeVoiceEntry } from "./DowntimeVoiceEntry";
import "./open-downtime-reason.css";
export function OpenDowntimeReason({ reasons, recentReasonIds = [], draftKey, preview, submit }: {
  reasons: ProductionReason[]; recentReasonIds?: string[]; draftKey: string; preview: boolean;
  submit: (input: DowntimeReasonRequest, signal: AbortSignal) => Promise<unknown>;
}) {
  const stored = useStoredOperatorDraft(draftKey, preview, { kind: "downtime", reasonCodeId: "", customMode: false, customReason: "", note: "" });
  const draft = reasonFormDraftSchema.parse(stored.data);
  const [saving, setSaving] = useState(false), [error, setError] = useState<string | null>(null), [voiceBusy, setVoiceBusy] = useState(false);
  const [noteOpen, setNoteOpen] = useState(false);
  const request = useRef<AbortController | null>(null), live = useRef(true);
  useEffect(() => { live.current = true; return () => { live.current = false; request.current?.abort(); }; }, []);
  const available = reasons.filter((reason) => reason.category === "DOWNTIME");
  const noteRequired = available.find((reason) => reason.id === draft.reasonCodeId)?.groupName === "Diğer";
  const save = async () => {
    const input = downtimeReasonSubmission(draft);
    if (request.current || voiceBusy || stored.loading || stored.error || !input || (noteRequired && !draft.note.trim())) return;
    const controller = new AbortController(); request.current = controller; setSaving(true); setError(null);
    try {
      const record = await stored.flush();
      const saved = reasonFormDraftSchema.parse(record.data);
      const savedInput = downtimeReasonRequest(saved, record.intentId);
      if (!savedInput) return;
      await submit(savedInput, controller.signal);
      await stored.clear();
    } catch (failure) { if (live.current) setError(failure instanceof Error ? failure.message : "Sonuç doğrulanamadı. Taslak korunuyor."); }
    finally { request.current = null; if (live.current) setSaving(false); }
  };
  return <section className="open-downtime-reason" aria-labelledby="open-downtime-title">
    <header><h2 id="open-downtime-title">Duruş nedeni</h2>
      <p>Konuşarak veya listeden seçerek belirtin.</p>
    </header>
    <form onSubmit={(event) => { event.preventDefault(); void save(); }}>
      <DowntimeVoiceEntry draftKey={draftKey} reasons={available} draft={draft} onChange={(next) => stored.change({ kind: "downtime", ...next })} disabled={saving || stored.loading} onBusyChange={setVoiceBusy}/>
      <DowntimeQuickReasons reasons={available} ids={recentReasonIds} selectedId={draft.reasonCodeId}
        disabled={saving || stored.loading || voiceBusy}
        onSelect={(reasonCodeId) => stored.change({ ...draft, reasonCodeId, customMode: false, customReason: "" })} />
      <DowntimeReasonChoice reasons={available} draft={draft} onChange={(next) => stored.change({ kind: "downtime", ...next })} disabled={saving || stored.loading || voiceBusy}/>

      {!noteOpen && !noteRequired && !draft.note ? <button type="button" className="touch-note-toggle"
        disabled={saving || stored.loading || voiceBusy} onClick={() => setNoteOpen(true)}>Açıklama ekle</button> : null}
      {noteOpen || noteRequired || draft.note ? <DowntimeKeyboardField label={<>Açıklama {noteRequired ? "(zorunlu)" : "(isteğe bağlı)"}</>}
        keyboardLabel="Açıklama için klavyeyi aç" multiline value={draft.note} maxLength={500}
        required={noteRequired} disabled={saving || stored.loading || voiceBusy}
        onChange={(note) => stored.change({ ...draft, note })} /> : null}
      {stored.error ? <p role="alert">{stored.error} <button type="button" onClick={stored.reload}>Taslağı yeniden yükle</button></p> : null}
      {error ? <p role="alert">{error}</p> : null}
      <footer className="open-downtime-footer">
        <p className="open-downtime-draft-status" role="status">
          {stored.loading ? "Taslak yükleniyor…" : stored.pending ? "Taslak korunuyor…" : "Henüz kaydedilmedi"}
        </p>
        <button type="submit" className="downtime-save-action"
          disabled={saving || stored.loading || voiceBusy || stored.pending || Boolean(stored.error) || !downtimeReasonSubmission(draft) || Boolean(noteRequired && !draft.note.trim())}>
          {saving ? "Kaydediliyor…" : "Nedeni kaydet"}
        </button>
      </footer>
    </form>
  </section>;
}
